/**
 * hovercraft_visuals.ts — CLIENT-ONLY per-model look of a hovercraft's
 * engines + dust (the catalog mirror stays byte-identical with the server, so
 * anything purely visual lives here, keyed by the same model keys).
 *
 * Every model gets a distinct engine signature — nozzle count, plume colour,
 * afterburner colour and dust throw — fitted to the actual hull by
 * `VehicleFx` from the model's bounding box (nozzles sit on the tail line,
 * spread across the hull's width; dust throws from the skirt's rear corners).
 */

export interface HovercraftVisual {
  /** Engine nozzles across the tail (1 = centre; n ≥ 2 spread ±spreadFrac × half-width). */
  nozzles: number;
  /** Fraction of the half-width the outermost nozzles sit at. */
  spreadFrac: number;
  /** Plume / core colour while cruising (hex). */
  color: number;
  /** Afterburner colour under SHIFT boost / a drift mini-turbo (hex). */
  boostColor: number;
  /** Plume length at cruise (m) — the jet's visible exhaust cone. */
  plumeLen: number;
  /** Nozzle mouth radius (m). */
  nozzleR: number;
  /** Dust throw multiplier (heavier skirts kick more). */
  dust: number;
}

export const HOVERCRAFT_VISUALS: Record<string, HovercraftVisual> = {
  hover_skiff:   { nozzles: 1, spreadFrac: 0,    color: 0xff8a3c, boostColor: 0xffe27a, plumeLen: 2.2, nozzleR: 0.34, dust: 1.15 },
  hover_dune:    { nozzles: 2, spreadFrac: 0.42, color: 0xff9d4a, boostColor: 0xfff0a8, plumeLen: 2.6, nozzleR: 0.30, dust: 1.3 },
  hover_falcon:  { nozzles: 2, spreadFrac: 0.5,  color: 0x4fc8ff, boostColor: 0xd8f6ff, plumeLen: 3.2, nozzleR: 0.28, dust: 0.9 },
  hover_raptor:  { nozzles: 3, spreadFrac: 0.55, color: 0x8a6cff, boostColor: 0xe6dcff, plumeLen: 3.6, nozzleR: 0.26, dust: 1.0 },
  hover_tempest: { nozzles: 4, spreadFrac: 0.6,  color: 0x9fe9ff, boostColor: 0xffffff, plumeLen: 4.2, nozzleR: 0.24, dust: 1.1 },
  // The 2026-10-08 wave (every model still burns its own colour — verify:vehicle-fx).
  hover_mule:    { nozzles: 2, spreadFrac: 0.55, color: 0xff7a2a, boostColor: 0xffd9a0, plumeLen: 2.0, nozzleR: 0.36, dust: 1.4 },
  hover_punt:    { nozzles: 1, spreadFrac: 0,    color: 0xffb347, boostColor: 0xfff1c2, plumeLen: 1.8, nozzleR: 0.26, dust: 0.8 },
  hover_strider: { nozzles: 2, spreadFrac: 0.7,  color: 0x3fd6c2, boostColor: 0xd6fff6, plumeLen: 2.4, nozzleR: 0.30, dust: 1.0 },
  hover_lark:    { nozzles: 2, spreadFrac: 0.45, color: 0xffe8c8, boostColor: 0xffffff, plumeLen: 2.6, nozzleR: 0.26, dust: 0.9 },
  hover_wasp:    { nozzles: 2, spreadFrac: 0.35, color: 0x35e0ff, boostColor: 0xe0fbff, plumeLen: 3.6, nozzleR: 0.30, dust: 0.9 },
  hover_ox:      { nozzles: 3, spreadFrac: 0.5,  color: 0xff6a1c, boostColor: 0xffc890, plumeLen: 2.4, nozzleR: 0.32, dust: 1.5 },
  hover_needle:  { nozzles: 1, spreadFrac: 0,    color: 0xbff5ee, boostColor: 0xffffff, plumeLen: 4.4, nozzleR: 0.34, dust: 0.8 }, // 0.34: the boost flare (1.9 × r) stays inside the 1.6 m hull's keel line
  hover_marshal: { nozzles: 2, spreadFrac: 0.5,  color: 0xffc24d, boostColor: 0xfff3c8, plumeLen: 3.2, nozzleR: 0.28, dust: 1.0 },
  hover_courser: { nozzles: 4, spreadFrac: 0.6,  color: 0xffd27a, boostColor: 0xfffbe8, plumeLen: 3.8, nozzleR: 0.24, dust: 1.1 },
  hover_kite:    { nozzles: 3, spreadFrac: 0.55, color: 0x7fd8ff, boostColor: 0xf0fbff, plumeLen: 4.0, nozzleR: 0.24, dust: 0.9 },
};

/** A visual for any key (unknown models get the skiff signature). */
export function hovercraftVisual(key: string): HovercraftVisual {
  return HOVERCRAFT_VISUALS[key] ?? HOVERCRAFT_VISUALS.hover_skiff;
}

// ─── THE SEAT (2026-10-08, the ships + vehicles wave) ────────────────────────
/**
 * Where a rider sits on a hovercraft, MEASURED on the served model (tools/asset-pipeline/out-ships-vehicles/
 * seat-measure.mjs — the top surface the client draws, box-fitted to 9 m; record: docs/research/2026-10-08-ships-vehicles/
 * PLAN.md "Phase 2"). In the fitted hull's own frame, about the model's origin (its box's bottom centre):
 *   fwd   metres AHEAD of the origin (negative = aft) — the seat's centre along the hull
 *   right metres to the craft's RIGHT (negative = left: a driver's seat beside a passenger's)
 *   pan   metres over the hull's lowest point of the surface the rider sits ON: a cushion's top; in a well with a level
 *         floor, or at a standing helm, the floor + RIDER_SEAT_M (the feet rest on it, the seat is the invisible one)
 * A craft WITHOUT a row is drawn exactly as it always was: the rider at the hull's centre, `hoverM` over the ground (the
 * five founding craft — their riders sit inside the hull). Client-only: the server knows `hoverM` and nothing of the
 * picture; the catalog's `hoverM` for a seated craft is `seatHoverM(seat)` (verify:vehicles pins it).
 */
export interface HoverSeat { fwd: number; right: number; pan: number }
/** The underside of a seated rider over their own root in the ride clip (`Chair_Sit_Idle_M`: the hips joint rides
 *  0.72–0.78 m up — player_clips_bed, 2026-10-08 — less the pelvis). */
export const RIDER_SEAT_M = 0.62;
/** A ride mesh's underside over the ground at rest (AtlasVehicleModule's rule for every hovercraft). */
export const HOVER_HULL_CLEAR_M = 0.15;
/** The rider's hips ride this far BEHIND their own root in that clip (the feet are the root; measured in the wave's
 *  bed: hips 0.45 m aft) — the seat goes under the HIPS, the feet land ahead of it. */
export const RIDER_HIPS_BACK_M = 0.45;
export const HOVER_SEATS: Readonly<Record<string, HoverSeat>> = {
  hover_mule:    { fwd: -0.48, right: 0, pan: 1.67 },
  hover_punt:    { fwd: -0.22, right: 0, pan: 1.71 },
  hover_strider: { fwd: -2, right: 0, pan: 3.2 },
  hover_lark:    { fwd: -0.91, right: -0.64, pan: 1.33 },
  hover_wasp:    { fwd: -2.21, right: 0, pan: 1.16 },
  hover_ox:      { fwd: -0.18, right: -0.67, pan: 1.83 },
  hover_needle:  { fwd: -1.95, right: 0, pan: 1.22 },
  hover_marshal: { fwd: -0.82, right: 0, pan: 2.12 },
  hover_courser: { fwd: -0.7, right: 0, pan: 2.21 },
  hover_kite:    { fwd: -0.92, right: 0, pan: 1.42 },
};
export function hoverSeat(key: string): HoverSeat | null { return HOVER_SEATS[key] ?? null; }
/** The catalog ride height that puts a seated rider ON this seat with the hull HOVER_HULL_CLEAR_M over the ground. */
export function seatHoverM(seat: HoverSeat): number { return Math.round((seat.pan - RIDER_SEAT_M + HOVER_HULL_CLEAR_M) * 100) / 100; }
/** Where the fitted, turned hull sits inside its ride group so that the seat lies under the rider's hips at the group's
 *  height (the nose is the group's −Z: a seat `fwd` ahead of the model's origin is at z = −fwd before the move, and the
 *  hips are at z = +RIDER_HIPS_BACK_M). The group rides at the rider's root, RIDER_SEAT_M up — and banks about that
 *  line, so the seat stays under the rider in a turn. */
export function seatHullOffset(seat: HoverSeat): { x: number; y: number; z: number } { return { x: -seat.right, y: -seat.pan, z: seat.fwd + RIDER_HIPS_BACK_M }; }
/** How far the hull's centre lies AHEAD of the rider's root (the wall probes move with the drawn hull). */
export function seatHullShift(seat: HoverSeat): number { return -(seat.fwd + RIDER_HIPS_BACK_M); }
